import PQueue from "p-queue"
import Worker from "./jpeg-encoder.thread.worker.ts?worker"
import type { AllocatedYUVBuffer, ThreadWorker } from "./jpeg-encoder.thread.worker"
import { transfer, wrap, type Remote } from "comlink"
import { clamp } from "@/utils/math"
import { Sentry } from "@/telemetry/sentry"
import { registerSentryContextTarget } from "@/telemetry/sentry-peers"
import type { PixelData } from "@/core/utils/image-data"
import { Deferred } from "@/utils/deferred"
import {
	type EncodedJPEGData,
	encodedJPEGDataToBlob,
	finalizeJpegBytes,
	type JpegEncoderLike,
	type RawMetadataEncodeJPEGOptions,
} from "@/core/encoders/jpeg-encoder"
import { assembleRestartedJpeg, prepareYUVBands } from "./parallel-jpeg"
import type { ChunkedJPEGBytes } from "./jpeg-chunks"

const MAX_ENCODING_THREADS_COUNT = 8
const DEFAULT_ENCODING_THREADS_COUNT = 4
const MEDIUM_HARDWARE_CONCURRENCY = 8
const HIGH_HARDWARE_CONCURRENCY = 14

export interface JpegEncoderOptions {
	encodingThreadsCount: number
}

export interface JpegYUVBufferLease extends JpegEncoderLike {
	data: Uint8Array
	release(): Promise<void>
}

interface ThreadEntry {
	worker: Worker
	thread: Remote<ThreadWorker>
	ready?: Promise<void>
}

export class JpegEncoderController implements JpegEncoderLike {
	private workerEntries: ThreadEntry[] = []
	private queue = new PQueue({ concurrency: 1 })

	private configuredEncodingThreadsCount = 1
	private idleTimer: ReturnType<typeof setTimeout> | null = null

	private sentryContext: Record<string, Record<string, unknown>> = {}

	public constructor(options: JpegEncoderOptions = { encodingThreadsCount: 1 }) {
		this.queue.on("idle", () => {
			this.scheduleIdleTermination()
		})
		this.queue.on("error", (error) => Sentry.captureException(error))
		this.setOptions(options)
	}

	public get encodingThreadsCount() {
		return this.configuredEncodingThreadsCount
	}

	public setSentryContext(context: { type: string; data: Record<string, unknown> }) {
		this.sentryContext[context.type] = context.data
		this.forEachWorker(({ thread }) => {
			void thread.setSentryContext(context)
		})
	}

	public disableSentry() {
		this.forEachWorker(({ thread }) => {
			void thread.disableSentry()
		})
	}

	public setOptions(options: JpegEncoderOptions) {
		const requestedEncodingThreadsCount = Math.floor(options.encodingThreadsCount)
		const encodingThreadsCount = clamp(
			Number.isNaN(requestedEncodingThreadsCount) ? 1 : requestedEncodingThreadsCount,
			1,
			MAX_ENCODING_THREADS_COUNT,
		)

		if (encodingThreadsCount === this.configuredEncodingThreadsCount) {
			return
		}

		if (this.queue.pending > 0 || this.queue.size > 0) {
			throw new Error("Cannot reconfigure the JPEG encoder while encoding is active")
		}

		this.configuredEncodingThreadsCount = encodingThreadsCount
		this.terminateWorkers()
	}

	public clearQueue() {
		this.queue.clear()
		if (this.queue.pending === 0) {
			this.terminateWorkers()
		}
	}

	public async releaseWorkers() {
		await this.queue.onIdle()

		if (this.queue.pending > 0 || this.queue.size > 0) {
			return false
		}

		this.terminateWorkers()
		return true
	}

	public encodeJPEG(pixelData: PixelData, options: RawMetadataEncodeJPEGOptions): Promise<Blob> {
		return this.encodeJPEGBytes(pixelData, options).then(encodedJPEGDataToBlob)
	}

	public encodeJPEGBytes(
		pixelData: PixelData,
		options: RawMetadataEncodeJPEGOptions,
	): Promise<EncodedJPEGData> {
		this.createWorkersIfNeeded()

		return this.queue.add(
			async () => {
				this.cancelIdleTermination()
				const workerEntries = this.getWorkerEntries()
				await this.ensureWorkersReady(workerEntries)
				return await this.encodeBandedJPEG(workerEntries, pixelData, options)
			},
			{ timeout: 30000 },
		)
	}

	public async acquireYUVBuffer(byteLength: number): Promise<JpegYUVBufferLease> {
		this.createWorkersIfNeeded()

		const leaseReady = new Deferred<JpegYUVBufferLease>()
		const leaseReleased = new Deferred<void>()
		const leaseFreed = new Deferred<void>()

		void this.queue
			.add(async () => {
				this.cancelIdleTermination()
				const workerEntries = this.getWorkerEntries()
				const owner = getOwnerWorker(workerEntries)
				let allocation: AllocatedYUVBuffer | null = null
				let encodeTask: Promise<EncodedJPEGData> | null = null
				let encodeSettled: Promise<void> = Promise.resolve()

				try {
					await this.ensureWorkersReady(workerEntries)
					allocation = await owner.thread.allocateYUVBuffer(byteLength)

					let released = false
					const release = async () => {
						if (!released) {
							released = true
							leaseReleased.resolve()
						}
						await leaseFreed.promise
					}

					const encodeAllocatedJPEGBytes = async (
						pixelData: PixelData,
						options: RawMetadataEncodeJPEGOptions,
					) => {
						if (!allocation || released) {
							throw new Error("Allocated JPEG YUV buffer has already been released")
						}
						if (encodeTask) {
							throw new Error("Allocated JPEG YUV buffer can only be encoded once")
						}

						encodeTask = this.encodeBandedJPEG(workerEntries, pixelData, options, {
							pointer: allocation.pointer,
							byteLength: allocation.byteLength,
						})

						encodeSettled = encodeTask.then(
							() => undefined,
							() => undefined,
						)
						return await encodeTask
					}

					leaseReady.resolve({
						data: allocation.bufferView,
						release,
						encodeJPEGBytes: encodeAllocatedJPEGBytes,
						encodeJPEG: async (pixelData, options) =>
							encodedJPEGDataToBlob(
								await encodeAllocatedJPEGBytes(pixelData, options),
							),
					})

					await leaseReleased.promise
				} catch (error) {
					leaseReady.reject(error)
					throw error
				} finally {
					await encodeSettled
					if (allocation) {
						await owner.thread.freeYUVBuffer(allocation.pointer).catch((error) => {
							Sentry.captureException(error)
						})
					}
					leaseFreed.resolve()
				}
			})
			.catch((error) => {
				leaseReady.reject(error)
			})

		return await leaseReady.promise
	}

	public async getTotalBytesUsage() {
		this.cancelIdleTermination()

		const byteLengths = await Promise.all(
			this.workerEntries.map(({ thread }) => thread.getTotalBytesUsage()),
		)

		if (this.workerEntries.length > 0 && this.queue.pending === 0 && this.queue.size === 0) {
			this.scheduleIdleTermination()
		}

		return byteLengths.reduce((total, byteLength) => total + byteLength, 0)
	}

	private async encodeBandedJPEG(
		workerEntries: readonly ThreadEntry[],
		pixelData: PixelData,
		options: RawMetadataEncodeJPEGOptions,
		allocation?: Pick<AllocatedYUVBuffer, "pointer" | "byteLength">,
	): Promise<EncodedJPEGData> {
		const buffer = pixelData.data.buffer
		const canShareInput =
			typeof SharedArrayBuffer !== "undefined" && buffer instanceof SharedArrayBuffer
		const prepared = prepareYUVBands(pixelData, workerEntries.length, !canShareInput)
		const encodedBands: Array<ChunkedJPEGBytes | undefined> = Array.from(
			{ length: prepared.bands.length },
			() => undefined,
		)

		const workerTasks = workerEntries.map(async ({ thread }, workerIndex) => {
			for (
				let bandIndex = workerIndex;
				bandIndex < prepared.bands.length;
				bandIndex += workerEntries.length
			) {
				const band = prepared.bands[bandIndex]
				if (!band) {
					throw new Error(`Parallel JPEG band ${bandIndex + 1} is unavailable`)
				}

				const bandOptions = { quality: options.quality }
				const encoded =
					allocation && workerIndex === 0
						? await thread.encodeAllocatedYUVJPEGBand(
								band.pixelData,
								allocation,
								bandOptions,
							)
						: await thread.encodeJPEGBand(
								transferPixelData(band.pixelData),
								bandOptions,
							)
				encodedBands[bandIndex] = encoded
			}
		})
		const settledWorkers = await Promise.allSettled(workerTasks)
		const failedWorker = settledWorkers.find(
			(result): result is PromiseRejectedResult => result.status === "rejected",
		)
		if (failedWorker) {
			throw failedWorker.reason
		}

		const completeBands = encodedBands.map((bytes, index) => {
			if (!bytes) {
				throw new Error(`Parallel JPEG worker did not return band ${index + 1}`)
			}
			return bytes
		})
		const assembledBytes = assembleRestartedJpeg(completeBands, {
			width: prepared.width,
			height: prepared.height,
			restartInterval: prepared.restartInterval,
		})

		return {
			bytes: finalizeJpegBytes(assembledBytes, pixelData, options),
			type: "image/jpeg",
		}
	}

	private ensureWorkersReady(workerEntries: readonly ThreadEntry[]) {
		return Promise.all(workerEntries.map((entry) => this.ensureThreadReady(entry))).then(
			() => undefined,
		)
	}

	private ensureThreadReady(entry: ThreadEntry) {
		entry.ready ??= entry.thread.loadLibraryIfNeeded()
		return entry.ready
	}

	private scheduleIdleTermination() {
		this.cancelIdleTermination()
		this.idleTimer = setTimeout(() => {
			this.idleTimer = null
			if (this.queue.pending === 0 && this.queue.size === 0) {
				this.terminateWorkers()
			}
		}, 1000)
	}

	private cancelIdleTermination() {
		if (this.idleTimer) {
			clearTimeout(this.idleTimer)
			this.idleTimer = null
		}
	}

	private getWorkerEntries() {
		if (this.workerEntries.length === 0) {
			throw new Error("JPEG encoder workers unavailable")
		}
		return this.workerEntries
	}

	private createWorkersIfNeeded() {
		this.cancelIdleTermination()
		if (this.workerEntries.length > 0) {
			return
		}

		for (
			let threadIndex = 0;
			threadIndex < this.configuredEncodingThreadsCount;
			threadIndex++
		) {
			const worker = new Worker()
			const thread = wrap<ThreadWorker>(worker)
			Object.entries(this.sentryContext).forEach(([type, data]) => {
				void thread.setSentryContext({ type, data })
			})
			this.workerEntries.push({ worker, thread })
		}
	}

	private terminateWorkers() {
		this.cancelIdleTermination()
		this.forEachWorker(({ worker }) => worker.terminate())
		this.workerEntries = []
	}

	private forEachWorker(callback: (entry: ThreadEntry) => void) {
		this.workerEntries.forEach(callback)
	}
}

function getOwnerWorker(workerEntries: readonly ThreadEntry[]) {
	const owner = workerEntries[0]
	if (!owner) {
		throw new Error("JPEG encoder has no owner worker")
	}
	return owner
}

function transferPixelData(pixelData: PixelData): PixelData {
	const buffer = pixelData.data.buffer
	if (typeof SharedArrayBuffer !== "undefined" && buffer instanceof SharedArrayBuffer) {
		return pixelData
	}
	if (buffer instanceof ArrayBuffer) {
		return transfer(pixelData, [buffer])
	}
	return pixelData
}

export function getJpegEncodingThreadsCount(hardwareConcurrency: unknown): number {
	if (
		typeof hardwareConcurrency !== "number" ||
		!Number.isFinite(hardwareConcurrency) ||
		hardwareConcurrency < 1
	) {
		return DEFAULT_ENCODING_THREADS_COUNT
	}

	if (hardwareConcurrency >= HIGH_HARDWARE_CONCURRENCY) {
		return 8
	}
	if (hardwareConcurrency >= MEDIUM_HARDWARE_CONCURRENCY) {
		return 4
	}
	return 1
}

export const JpegEncoder = new JpegEncoderController({
	encodingThreadsCount: getJpegEncodingThreadsCount(
		typeof navigator === "undefined" ? undefined : navigator.hardwareConcurrency,
	),
})

registerSentryContextTarget(JpegEncoder)
